import { Component, computed, inject, signal } from '@angular/core';
import { BaseModalComponent } from "../../../../../core/components/modals/base-modal/base-modal.component";
import {
  AttachDetachProductsBodyParams,
  AttachDetachProductsParams,
  GetProductsQueryParams,
  ProductCardGroupModalDialogData,
  WildberriesProductCardData
} from "./types/product-card-group-modal.types";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatButton } from "@angular/material/button";
import { ProductCardGroupModalService } from "./services/product-card-group-modal.service";
import { CarouselComponent } from "../../../../../shared/components/ui/carousel/carousel.component";
import { MatFormField, MatInput, MatLabel } from "@angular/material/input";
import { MatTab, MatTabChangeEvent, MatTabGroup } from "@angular/material/tabs";
import { ProductCardGroupItemComponent } from "./components/product-card-group-item/product-card-group-item.component";
import { LoaderComponent } from "../../../../../shared/components/loader/loader.component";
import { FormsModule } from "@angular/forms";
import { tabsData } from "./configs/product-card-group-modal-config";

@Component({
  selector: 'app-product-card-group-modal',
  imports: [
    BaseModalComponent,
    LoaderComponent,
    MatButton,
    CarouselComponent,
    MatFormField,
    MatInput,
    MatLabel,
    MatTab,
    MatTabGroup,
    ProductCardGroupItemComponent,
    FormsModule
  ],
  templateUrl: './product-card-group-modal.component.html',
  styleUrl: './product-card-group-modal.component.scss'
})
export class ProductCardGroupModalComponent extends BaseModalComponent {
  private productCardGroupModalDialogData = inject<ProductCardGroupModalDialogData>(MAT_DIALOG_DATA);
  private productCardGroupModalService: ProductCardGroupModalService = inject(ProductCardGroupModalService);

  public readonly tabsData = tabsData;
  private attachedProductsData = signal<Array<WildberriesProductCardData>>([]);
  private unattachedProductsData = signal<Array<WildberriesProductCardData>>([]);
  public selectedTabIndex = signal<number>(0);
  public selectedData = signal<Array<number>>([]);
  public currentIndex = signal<number>(0);
  public searchTerm = signal<string>('');
  public mainArticleId = computed(() => Number(this.productCardGroupModalDialogData.productCardGroupModalDto.id));

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.productCardGroupModalDialogData.columnKey);
  }

  public getDataByTab(): Array<WildberriesProductCardData> {
    return this.selectedTabIndex() === 0 ? this.attachedProductsData() : this.unattachedProductsData();
  }

  public handleIndexChange(index: number): void {
    this.currentIndex.set(index);
  }

  public handleSearch(value: string): void {
    this.searchTerm.set(value);
  }

  public async handleEnterSearch(): Promise<void> {
    this.selectedTabIndex() === 1 ?
      await this.loadUnattachedProducts() :
      await this.loadAttachedProducts();
  }

  public async handleTabChange(event: MatTabChangeEvent): Promise<void> {
    this.selectedTabIndex.set(event.index);
    this.selectedData.set([]);
    this.searchTerm.set('');
    this.loadingService.setIsLoadingState(true);
    const handlers: Record<number, () => Promise<void>> = {
      0: () => this.loadAttachedProducts(),
      1: () => this.loadUnattachedProducts()
    };

    await handlers[this.selectedTabIndex()]?.();
  }

  public async handleBtn(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    const handlers: Record<number, () => Promise<void>> = {
      0: () => this.detachProducts(),
      1: () => this.attachProducts()
    };

    await handlers[this.selectedTabIndex()]?.();
  }

  private async initModal(): Promise<void> {
    this.modalStatesService.openModal(this.productCardGroupModalDialogData.columnKey)
    await this.loadAttachedProducts();
  }

  private async loadAttachedProducts(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    await this.productCardGroupModalService.getAttachedProducts(this.mainArticleId())
      .then((data) => this.attachedProductsData.set(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private async loadUnattachedProducts(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    const params = this.getProductsQueryParams();
    await this.productCardGroupModalService.getUnattachedProducts(params)
      .then((data) => this.unattachedProductsData.set(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private getProductsQueryParams(): GetProductsQueryParams {
    return {
      mainArticleId: this.mainArticleId(),
      ...(this.searchTerm() && { search: this.searchTerm() })
    };
  }

  private async detachProducts(): Promise<void> {
    await this.productCardGroupModalService.detachProducts(this.getAttachDetachProductsBodyParams())
      .then((data) => this.attachedProductsData.set(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private async attachProducts(): Promise<void> {
    const params: AttachDetachProductsParams = {
      body: this.getAttachDetachProductsBodyParams(),
      queryParams:  this.getProductsQueryParams()
    };

    await this.productCardGroupModalService.attachProducts(params)
      .then((data) => this.unattachedProductsData.set(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private getAttachDetachProductsBodyParams(): AttachDetachProductsBodyParams {
    const [ first, second, third ] = this.selectedData();

    return {
      mainArticleId: this.mainArticleId(),
      firstArticleId: first,
      secondArticleId: second,
      thirdArticleId: third
    };
  }
}
